/**
 * UnassignedColumnsRow — the fallback bucket for columns that belong to no
 * entity-type section. Rendered at the very end of the multi-type mapping,
 * after all entity-type sections and pills.
 *
 * Each column is shown as a table row with a kebab menu offering "Add to
 * {entity type}" actions. The section uses a warning background to draw
 * attention to columns that won't be imported. It remains a droppable target
 * so drag-and-drop still works alongside the menu.
 */

import { useDraggable, useDroppable } from '@dnd-kit/core';
import { AlertTriangle } from '@untitledui/icons';
import { useCallback } from 'react';

import { DropArea } from '@/lib/base/card';
import { GripVertical } from '@/lib/base/foundations/grip-vertical';
import { cx } from '@/utils/cx';

import type { MoveTarget } from './ColumnActionsMenu';
import { ColumnActionsMenu } from './ColumnActionsMenu';

interface UnassignedColumnsRowProps {
  /** Droppable id — `unassignedVirtualKey(fileId)`. */
  unassignedKey: string;
  /** Columns currently assigned to no entity-type section. */
  columns: string[];
  /** All entity types the column can be added to. */
  moveTargets: MoveTarget[];
  /** Called when the user picks "Add to {type}" from the row menu. */
  onAddTo: (column: string, targetVirtualKey: string) => void;
}

export function UnassignedColumnsRow({
  unassignedKey,
  columns,
  moveTargets,
  onAddTo,
}: UnassignedColumnsRowProps) {
  const { setNodeRef, isOver } = useDroppable({ id: unassignedKey });

  return (
    <DropArea
      nodeRef={setNodeRef}
      isOver={isOver}
      showBorder
      ariaLabel='Unassigned columns – columns dropped here will not be imported'
    >
      <div className='bg-utility-warning-50 px-4 py-3'>
        <div className='flex items-center gap-2'>
          <AlertTriangle
            className='size-4 text-utility-warning-600'
            aria-hidden='true'
          />
          <span className='text-sm font-medium text-utility-warning-700'>
            Unassigned columns
          </span>
          <span className='text-xs text-utility-warning-600'>
            ({columns.length}) – will not be imported
          </span>
        </div>
        {columns.length > 0 ? (
          /* A draggable list, not a table: the three cells were
             [handle][name][menu] — a row's controls, not columns of data. It is
             also a dnd-kit surface, and `lib/base/Table` is a react-aria grid
             whose own arrow-key focus model would fight the drag handles. */
          <ul className='mt-2 w-full'>
            {columns.map(column => (
              <UnassignedColumnRow
                key={column}
                column={column}
                dragId={`${unassignedKey}::${column}`}
                moveTargets={moveTargets}
                onAddTo={onAddTo}
              />
            ))}
          </ul>
        ) : (
          <p className='mt-1 text-xs text-utility-warning-600'>
            All columns are assigned. Drop a column here to exclude it from the
            import.
          </p>
        )}
      </div>
    </DropArea>
  );
}

// ── UnassignedColumnRow ───────────────────────────────────────────────────
// Single row for an unassigned column: drag handle + name + "Add to" menu.

interface UnassignedColumnRowProps {
  column: string;
  dragId: string;
  moveTargets: MoveTarget[];
  onAddTo: (column: string, targetVirtualKey: string) => void;
}

function UnassignedColumnRow({
  column,
  dragId,
  moveTargets,
  onAddTo,
}: UnassignedColumnRowProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
  });

  // Prevent pointer events on the dropdown cell from starting a drag.
  const stopDrag = useCallback((e: React.PointerEvent) => {
    e.stopPropagation();
  }, []);

  return (
    <li
      ref={setNodeRef}
      className={cx(
        'flex items-center gap-2 border-b border-utility-warning-200 py-1.5 last:border-b-0 cursor-grab touch-none active:cursor-grabbing',
        isDragging && 'opacity-40'
      )}
      {...listeners}
      {...attributes}
    >
      <GripVertical
        className='size-4 shrink-0 text-utility-warning-400'
        aria-hidden='true'
      />
      <span className='min-w-0 flex-1 text-sm text-fg-primary'>{column}</span>
      <span className='w-8 shrink-0' onPointerDown={stopDrag}>
        <ColumnActionsMenu
          column={column}
          moveTargets={moveTargets}
          onMoveTo={onAddTo}
          showRemove={false}
        />
      </span>
    </li>
  );
}
